<template>
  <b-modal
    v-model="isVisible"
    centered
    hide-header
    hide-header-close
    hide-footer
    content-class="!rounded-[12px]"
    body-class="!p-[1.5rem]"
    @hide="onModalHide"
    @hidden="onHidden"
  >
    <h4 class="!text-[18px] font-medium mb-[0.25rem] text-[#0A0A0A]">
      Hapus Kategori
    </h4>
    <p class="mb-[1.75rem] text-[14px] text-[#737373]">
      Tindakan ini akan menghapus kategori secara permanen<template v-if="!isEmpty">
        , dan artikel didalamnya akan diubah menjadi <strong>"{{ selectedCategoryName }}"</strong>
      </template>.
    </p>
    <ValidationObserver
      ref="formObserver"
      v-slot="{ invalid }"
    >
      <div
        v-if="!isEmpty"
        ref="selectContainer"
        class="mb-[1.75rem]"
      >
        <label class="text-[#0A0A0A] text-[14px] mb-[0.75rem] block font-medium">Pilih Kategori <span class="text-[#DC2626]">*</span></label>
        <ValidationProvider
          v-slot="{ errors }"
          name="Kategori pengganti"
          rules="required"
        >
          <InfiniteSelect
            v-model="selectedCategory"
            :options="categories"
            option-label="name"
            placeholder="Pilih Kategori"
            :has-next-page="hasNextPageCategory"
            :loading="isFetchingCategories"
            :clearable="false"
            :class="[ errors.length > 0 ? 'border-[#DC2626] border rounded-[4px]' : '' ]"
            @on-search="onSearchCategory"
            @on-intersect="onIntersectCategory"
          />
          <small
            v-if="errors.length > 0"
            class="mt-[0.75rem] block text-[14px] text-[#DC2626]"
          >
            {{ errors[0] }}
          </small>
        </ValidationProvider>
      </div>

      <div class="flex justify-end items-center mt-[1.25rem] gap-[0.5rem]">
        <b-button
          variant="ghost"
          size="sm"
          class="!text-[#737373] !font-normal !px-[1rem] !border-[1px] !border-[#E5E5E5] !py-[0.65rem] !shadow-[0px_1px_2px_0px_#0000001A] !rounded-[8px]"
          :disabled="isLoading"
          @click="close"
        >
          Batal
        </b-button>
        <b-button
          :variant="!invalid && selectedCategory && !isLoading ? 'primary' : 'secondary'"
          class="!rounded-[8px] !px-[1rem] !py-[0.65rem] !flex items-center justify-center gap-[8px]"
          size="sm"
          :disabled="invalid || !selectedCategory || isLoading"
          @click="submit"
        >
          <b-spinner
            v-if="isLoading"
            small
          />
          <span>Hapus</span>
        </b-button>
      </div>
    </ValidationObserver>
  </b-modal>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { InfiniteSelect } from '@/views/components/select'
import { createPaginatedFetcher } from '@/@core/libs/fetch/usePaginatedFetcher'

export default {
  name: 'CategoryDeleteDialog',
  components: {
    ValidationProvider,
    ValidationObserver,
    InfiniteSelect,
  },
  data() {
    return {
      isVisible: false,
      isLoading: false,
      isEmpty: false,
      onSubmitCallback: null,
      deletedCategoryId: null,
      deletedArticleCount: 0,

      // Select state
      selectedCategory: null,
      categorySearch: '',
      categoryFetcher: null,
    }
  },
  computed: {
    /**
     * List of categories filtered by search, with the deleted category disabled.
     */
    categories() {
      const data = this.categoryFetcher?.data || []
      const filteredData = this.categorySearch
        ? data.filter(c => c.name && c.name.toLowerCase().includes(this.categorySearch.toLowerCase()))
        : data

      return filteredData.map(c => ({
        ...c,
        disabled: c.id === this.deletedCategoryId,
      }))
    },
    isFetchingCategories() {
      return this.categoryFetcher?.isLoading || false
    },
    hasNextPageCategory() {
      return this.categoryFetcher?.hasMore || false
    },
    /**
     * Gets the name of the selected fallback category.
     */
    selectedCategoryName() {
      return this.selectedCategory?.name || 'Tanpa Kategori'
    },
  },
  created() {
    this.categoryFetcher = createPaginatedFetcher(async params => {
      const { data } = await this.$http_new.get('/live-chat/api/v1/categories/options', { params })
      return { data: data.data }
    })
  },
  methods: {
    /**
     * Opens dialog, resets state, and fetches categories.
     * @param {Object} options - Dialog options (deletedCategory, onSubmit).
     */
    async show(options = {}) {
      this.onSubmitCallback = options.onSubmit || null
      this.deletedCategoryId = options.deletedCategory?.id || null
      this.deletedArticleCount = options.deletedCategory?.article_count || 0
      this.isVisible = true
      this.isEmpty = this.deletedArticleCount <= 0

      // Reset state
      this.selectedCategory = null
      this.categorySearch = ''

      await this.categoryFetcher.fetchData({ reset: true })

      if (!this.selectedCategory) {
        const defaultCategory = this.categories.find(c => c.name && c.name.toLowerCase() === 'tanpa kategori')
        this.selectedCategory = defaultCategory || { name: 'Tanpa Kategori' }
      }

      this.$nextTick(() => {
        if (this.$refs.formObserver) {
          this.$refs.formObserver.reset()
        }
      })
    },
    /**
     * Resets state when dialog hides.
     */
    onHidden() {
      this.selectedCategory = null
      this.categorySearch = ''
      if (this.$refs.formObserver) {
        this.$refs.formObserver.reset()
      }
    },
    /**
     * Prevents backdrop from closing the modal when a v-select dropdown (.vs__dropdown-menu) is open.
     */
    onModalHide(event) {
      if (event.trigger === 'backdrop') {
        const dropdown = this.$refs.selectContainer?.querySelector('.vs__dropdown-menu')
        if (dropdown) {
          event.preventDefault()
        }
      }
    },
    close() {
      this.isVisible = false
    },
    /**
     * Validates and submits fallback category.
     */
    submit() {
      this.$refs.formObserver.validate().then(async success => {
        if (success && typeof this.onSubmitCallback === 'function') {
          try {
            this.isLoading = true
            await this.onSubmitCallback(this.selectedCategory)

            let message = 'Kategori berhasil dihapus'
            if (this.deletedArticleCount > 0) {
              message += ` dan ${this.deletedArticleCount} artikel berhasil dipindahkan ke ${this.selectedCategoryName}`
            }

            this.$toast_success({ message })
            this.close()
          } catch (error) {
            this.$toast_error({ message: 'Gagal menghapus kategori, silahkan coba kembali' })
          } finally {
            this.isLoading = false
          }
        }
      })
    },
    onSearchCategory(query) {
      this.categorySearch = query
    },
    onIntersectCategory() {
      // TODO: for now, we doesn't use infinite fetch for category, but we keep this method for future use
      // if (this.categoryFetcher && this.categoryFetcher.hasMore && !this.categoryFetcher.isLoading) {
      //   this.categoryFetcher.fetchData()
      // }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
